/**
 * ============================================================
 * dealEvents.ts — Quando as coisas ACONTECERAM com um lead
 * ============================================================
 *
 * O filtro de período histórico recortava por `createdAt` (coorte): "leads
 * criados no período, no estágio em que estão hoje". Para o gerente, a
 * pergunta é outra — o que aconteceu no mês: quantos entraram em proposta,
 * quantos ganharam, quantos perderam, quantas reuniões. Esses eventos vivem
 * em `statusHistory` (mantido pelo trigger de coluna), em `contactLogs` e em
 * `appointments`. Este módulo lê esses carimbos de tempo; a semântica de
 * "qual coluna é ganho" vem de lib/stageSemantics.
 * ============================================================
 */
import type { Deal } from '@/store/types';

export interface DateRange { from: Date; to: Date }

const inRange = (iso: string | null | undefined, range: DateRange): boolean => {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return false;
  return t >= range.from.getTime() && t <= range.to.getTime();
};

/** Lead criado dentro do período. */
export function dealCreatedIn(deal: Deal, range: DateRange): boolean {
  return inRange(deal.createdAt, range);
}

/**
 * Data em que o lead entrou pela última vez em alguma das colunas `ids`.
 * Usa `statusHistory` ({from,to,date}); se não houver histórico (lead antigo,
 * importado, movido por API antes do trigger) e o lead ESTÁ numa das colunas,
 * usa `lastStatusDate`.
 */
export function stageEntryDate(deal: Deal, ids: Set<string>): string | null {
  const history = deal.statusHistory || [];
  for (let i = history.length - 1; i >= 0; i--) {
    const h = history[i];
    if (h && ids.has(h.to) && h.date) return h.date;
  }
  if (ids.has(deal.column)) return deal.lastStatusDate || deal.createdAt || null;
  return null;
}

/** O lead entrou em alguma das colunas `ids` DENTRO do período. */
export function enteredStageIn(deal: Deal, ids: Set<string>, range: DateRange): boolean {
  const history = deal.statusHistory || [];
  for (const h of history) {
    if (h && ids.has(h.to) && inRange(h.date, range)) return true;
  }
  if (history.length === 0 && ids.has(deal.column)) {
    return inRange(deal.lastStatusDate || deal.createdAt, range);
  }
  return false;
}

/** Qualquer sinal de vida no período: criado, movido ou contato registrado. */
export function dealTouchedIn(deal: Deal, range: DateRange): boolean {
  if (inRange(deal.createdAt, range) || inRange(deal.lastStatusDate, range)) return true;
  if ((deal.statusHistory || []).some((h) => inRange(h?.date, range))) return true;
  if ((deal.contactLogs || []).some((l) => inRange(l?.date, range))) return true;
  return false;
}

/** Dias entre a criação e a entrada em ganho; null se não ganhou. */
export function dealCycleDays(deal: Deal, wonIds: Set<string>): number | null {
  const won = stageEntryDate(deal, wonIds);
  if (!won || !deal.createdAt) return null;
  const days = (new Date(won).getTime() - new Date(deal.createdAt).getTime()) / 86400000;
  return Number.isFinite(days) && days >= 0 ? Math.round(days) : null;
}

/**
 * Data do último contato ou null. Desde 15/09 o banco mantém
 * `leads.last_contact_at` (a partir de `lead_contact_logs`, fuso de
 * Brasília); o JSONB `contactLogs` fica como reserva para o lead recém
 * criado no cliente ou ainda não sincronizado.
 */
export function lastContactDate(deal: Deal): string | null {
  if (deal.lastContactAt) return deal.lastContactAt;
  const logs = deal.contactLogs || [];
  let best: string | null = null;
  for (const l of logs) {
    if (!l?.date) continue;
    if (!best || new Date(l.date).getTime() > new Date(best).getTime()) best = l.date;
  }
  return best;
}

/** Dias desde o último contato; se nunca houve contato, desde a criação. */
export function daysSinceLastContact(deal: Deal, now: Date = new Date()): number {
  const ref = lastContactDate(deal) || deal.lastStatusDate || deal.createdAt;
  if (!ref) return 0;
  return Math.max(0, Math.floor((now.getTime() - new Date(ref).getTime()) / 86400000));
}

/**
 * Tempo (dias) que o lead passou em cada coluna, a partir do histórico.
 * O estágio atual conta até `now`. Sem histórico, tudo vai para a coluna atual.
 */
export function daysPerStage(deal: Deal, now: Date = new Date()): Map<string, number> {
  const out = new Map<string, number>();
  const history = (deal.statusHistory || []).filter((h) => h?.date);
  const add = (col: string, ms: number) => out.set(col, (out.get(col) || 0) + Math.max(0, ms / 86400000));
  let cursorCol = history[0]?.from || deal.column;
  let cursorTime = deal.createdAt ? new Date(deal.createdAt).getTime() : now.getTime();
  for (const h of history) {
    const t = new Date(h.date).getTime();
    if (!Number.isNaN(t)) {
      add(cursorCol, t - cursorTime);
      cursorTime = t;
    }
    cursorCol = h.to;
  }
  add(cursorCol, now.getTime() - cursorTime);
  return out;
}
